import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DriverBulkUploadResult } from '@/types'

interface DriverCompleteStepProps {
  result: DriverBulkUploadResult | null
  onNewUpload: () => void
  onGoToDrivers: () => void
}

export function DriverCompleteStep({
  result,
  onNewUpload,
  onGoToDrivers
}: DriverCompleteStepProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Bearbetning slutförd</CardTitle>
          <Badge variant="outline">Klart</Badge>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryItem label="Totala rader" value={result?.totalRows ?? 0} />
          <SummaryItem label="Skapade förare" value={result?.driversCreated ?? 0} />
          <SummaryItem label="Uppdaterade förare" value={result?.driversUpdated ?? 0} />
          <SummaryItem label="Hoppade över" value={result?.driversSkipped ?? 0} />
          <SummaryItem label="Fält uppdaterade" value={result?.fieldsUpdated ?? 0} />
          <SummaryItem label="Fel" value={result?.errors.length ?? 0} variant={result?.errors.length ? 'destructive' : 'default'} />
        </CardContent>
      </Card>

      {result?.errors.length ? (
        <Card>
          <CardHeader>
            <CardTitle>Fel vid uppdatering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {result.errors.map((error, index) => (
              <div key={`${error.rowIndex}-${index}`} className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                Rad {error.rowIndex + 1}: {error.error}
                {error.driverName && ` (${error.driverName})`}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Inga fel rapporterades. Alla valda uppdateringar har genomförts.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" onClick={onGoToDrivers}>
          Gå till förare
        </Button>
        <Button onClick={onNewUpload}>
          Ny uppladdning
        </Button>
      </div>
    </div>
  )
}

function SummaryItem({
  label,
  value,
  variant = 'default'
}: {
  label: string
  value: number
  variant?: 'default' | 'destructive'
}) {
  const variantClasses = variant === 'destructive'
    ? 'border-destructive text-destructive'
    : 'border-primary/20 text-primary'

  return (
    <div className={`rounded-lg border bg-white p-3 ${variantClasses}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  )
}

